import {
  CURATOR_INVITE_TTL_DAYS,
  curatorInviteSchema,
  type CuratorInviteInput,
} from '@remote-medium/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  errorMessage,
} from '@remote-medium/ui';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

const DEFAULTS: CuratorInviteInput = { fullName: '', email: '' };

// The invitee gets a link to set their password; until then they show as
// Invited and can't reach the dashboard.
export function InviteCurator({
  onInvite,
}: {
  onInvite: (input: CuratorInviteInput) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState } = useForm<CuratorInviteInput>({
    resolver: zodResolver(curatorInviteSchema),
    defaultValues: DEFAULTS,
  });
  const errors = formState.errors;
  const busy = formState.isSubmitting;

  const submit = handleSubmit(async (values) => {
    setError(null);
    try {
      await onInvite(values);
      setOpen(false);
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (next) reset(DEFAULTS);
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger render={<Button size="sm" />}>
        <UserPlus data-icon="inline-start" />
        Invite curator
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Invite a curator</DialogTitle>
            <DialogDescription>
              They get an email with a link to set their password. The link works for{' '}
              {CURATOR_INVITE_TTL_DAYS} days, and you are recorded as the curator who invited them.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="invite-name">Full name</FieldLabel>
              <Input
                id="invite-name"
                autoComplete="off"
                aria-invalid={!!errors.fullName}
                {...register('fullName')}
              />
              {errors.fullName && <FieldError errors={[errors.fullName]} />}
            </Field>
            <Field>
              <FieldLabel htmlFor="invite-email">Email</FieldLabel>
              <Input
                id="invite-email"
                type="email"
                autoComplete="off"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && <FieldError errors={[errors.email]} />}
            </Field>
          </FieldGroup>
          {error && <p className="m-0 text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" disabled={busy} />}>
              Cancel
            </DialogClose>
            <Button type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
